import React, { useState, useEffect } from "react";
import { Button, Select, DatePicker } from "antd";
import moment from "moment";
import { VARIABLES, PERIOD_TYPES, ALL_CENTERS, COMPARE_API_BASE } from "./compareConstants";
import "./CompareFilterPanel.css";

const { Option } = Select;

const DEFAULT_FILTERS = {
  periodType: "daily",
  varId:      "110",
  date1:      moment().subtract(2, "days").format("YYYY-MM-DD"),
  date2:      moment().subtract(1, "days").format("YYYY-MM-DD"),
  centers:    "all",
  region:     null,
  country:    null,
};

const Field = ({ label, children }) => (
  <div className="cfp-field">
    <label className="cfp-label">{label}</label>
    {children}
  </div>
);

const CompareFilterPanel = ({ filters, onChange, onApply, loading }) => {
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const url = filters.region
      ? `${COMPARE_API_BASE}/countries/?region=${encodeURIComponent(filters.region)}`
      : `${COMPARE_API_BASE}/countries/`;
    fetch(url)
      .then(r => r.json())
      .then(data => setCountries(Array.isArray(data) ? data : []))
      .catch(() => setCountries([]));
  }, [filters.region]);

  const disabledDate = d => d && d.isAfter(moment().subtract(1, "days"));
  const updateField  = (key, value) => onChange({ ...filters, [key]: value });
  const handleReset  = () => onChange({ ...DEFAULT_FILTERS, metric: filters.metric });

  const handleRegionChange = (v) => {
    onChange({ ...filters, region: v || null, country: null });
  };

  const selectedCenters = filters.centers === "all"
    ? ALL_CENTERS
    : (filters.centers || ALL_CENTERS);

  const toggleCenter = (c) => {
    if (c === "all") {
      updateField("centers", "all");
      return;
    }
    const current = [...selectedCenters];
    const next = current.includes(c)
      ? current.filter(x => x !== c)
      : [...current, c];
    updateField("centers", next.length === ALL_CENTERS.length ? "all" : next.length ? next : "all");
  };

  const allSelected = filters.centers === "all" || selectedCenters.length === ALL_CENTERS.length;
  const canApply    = filters.date1 && filters.date2 && filters.varId && filters.periodType;

  const comparingText = (filters.date1 && filters.date2)
    ? `Comparing ${filters.date1} vs ${filters.date2}`
    : null;

  return (
    <div className="cfp-root">
      <div className="cfp-header">
        <span className="cfp-title">Filters</span>
        <button className="cfp-reset" onClick={handleReset}>Reset all</button>
      </div>

      <div className="cfp-subtitle">SYNOP land surface</div>
      {comparingText && <div className="cfp-comparing">{comparingText}</div>}

      <div className="cfp-body">
        <Field label="Period Type">
          <Select
            value={filters.periodType}
            onChange={v => {
              const updated = { ...filters, periodType: v };
              if (v === "monthly") {
                if (updated.date1) updated.date1 = moment(updated.date1).startOf("month").format("YYYY-MM-DD");
                if (updated.date2) updated.date2 = moment(updated.date2).startOf("month").format("YYYY-MM-DD");
              }
              onChange(updated);
            }}
            className="cfp-select"
            size="small"
          >
            {PERIOD_TYPES.map(p => (
              <Option key={p.value} value={p.value}>{p.label}</Option>
            ))}
          </Select>
        </Field>

        <Field label="Period 1">
          <DatePicker
            value={filters.date1 ? moment(filters.date1) : null}
            onChange={d => updateField("date1", d ? (filters.periodType === "monthly" ? d.startOf("month").format("YYYY-MM-DD") : d.format("YYYY-MM-DD")) : null)}
            disabledDate={disabledDate}
            picker={filters.periodType === "monthly" ? "month" : "date"}
            format={filters.periodType === "monthly" ? "YYYY-MM" : "YYYY-MM-DD"}
            className="cfp-datepicker"
            size="small"
            allowClear={false}
            placeholder="Select date"
          />
        </Field>

        <Field label="Period 2">
          <DatePicker
            value={filters.date2 ? moment(filters.date2) : null}
            onChange={d => updateField("date2", d ? (filters.periodType === "monthly" ? d.startOf("month").format("YYYY-MM-DD") : d.format("YYYY-MM-DD")) : null)}
            disabledDate={disabledDate}
            picker={filters.periodType === "monthly" ? "month" : "date"}
            format={filters.periodType === "monthly" ? "YYYY-MM" : "YYYY-MM-DD"}
            className="cfp-datepicker"
            size="small"
            allowClear={false}
            placeholder="Select date"
          />
        </Field>

        <div className="cfp-divider" />

        <Field label="Monitoring Centre">
          <Select
            value={filters.centers === "all" ? "all" : (Array.isArray(filters.centers) ? filters.centers[0] : filters.centers)}
            onChange={(v) => updateField("centers", v || "all")}
            className="cfp-select"
            size="small"
            placeholder="All centres"
          >
            <Option value="all">All</Option>
            {ALL_CENTERS.map(c => (
              <Option key={c} value={c}>{c}</Option>
            ))}
          </Select>
        </Field>

        <Field label="Region / RA">
          <Select
            value={filters.region || undefined}
            onChange={handleRegionChange}
            className="cfp-select"
            size="small"
            allowClear
            placeholder="All regions"
          >
            <Option value="Africa">RA I – Africa</Option>
            <Option value="Asia">RA II – Asia</Option>
            <Option value="South America">RA III – South America</Option>
            <Option value="North America, Central America and the Caribbean">RA IV – North America, Central America and the Caribbean</Option>
            <Option value="South-West Pacific">RA V – South-West Pacific</Option>
            <Option value="Europe">RA VI – Europe</Option>
            <Option value="Antarctica">Antarctica</Option>
          </Select>
        </Field>

        <Field label="Country (optional)">
          <Select
            value={filters.country || undefined}
            onChange={v => updateField("country", v || null)}
            className="cfp-select"
            size="small"
            allowClear
            placeholder="All"
            showSearch
            filterOption={(input, opt) =>
              opt.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {countries.map(c => (
              <Option key={c.code} value={c.code}>{c.name}</Option>
            ))}
          </Select>
        </Field>

        <Field label="Variable">
          <Select
            value={filters.varId}
            onChange={v => updateField("varId", v)}
            className="cfp-select"
            size="small"
          >
            {VARIABLES.map(v => (
              <Option key={v.value} value={v.value}>{v.label}</Option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="cfp-apply-area">
        <Button
          type="primary"
          onClick={onApply}
          loading={loading}
          disabled={!canApply}
          block
          className="cfp-apply-btn"
        >
          Apply Comparison
        </Button>
      </div>

      <div className="cfp-about">
        Dots show how many WDQMS category steps each station moved between Period 1 and Period 2 for the selected metric.
      </div>
    </div>
  );
};

export default CompareFilterPanel;
